// ================================================================================================
// Localization
// --------
// Global subsystem for localization translation
//
// TODO: Mark the changes labels as a modified and have UI presentation of them
// ================================================================================================
import _ from "lodash";
import { JSONSafeStringify, isDefined } from "@Utils/Micro";
import { DebugEx } from "@Utils/DebugEx";
import { WebNode } from '@API/WebNode';
import { setGlobal } from '@Utils/Globals';

import { ImageViewerStatics } from '../ImageViewer';
import { ReactTableStatics } from '@UI/Tables/ReactTable';
import { JsonSchemaValidator } from "@Utils/JsonSchemaValidator";
import { GetDateRangeToolItemLocalizationDictionary } from '@Primitive/DateRangeToolItemLocalization';
import { GetDateRangePickerLocalizationDictionary } from '@Primitive/ReactBootstrapDateRangePickerLocalization';
import { CalendarLocalizationTextMapKey, GetCalendarLocalizationDictionary } from '@Primitive/CalendarLocalization';
import { CVViewerWidgetMapKey, GetCVViewerWidgetLocalizationDictionary } from '@UI/Widgets/Things/Image/CVViewerLocalization';
import { GetSelectLocalizationDictionary, SelectLocalizationTextMapKey } from '@Primitive/SelectLocalization';
import { GetQueryBuilderLocalizationDictionary, QueryBuilderLocalizationTextMapKey } from '@Primitive/QueryBuilderLocalization';
import { GetVideoPlayerLocalizationDictionary, VideoPlayerLocalizationTextMapKey } from '@UI/Widgets/Things/Image/VideoPlayerLocalization';
import { LeafletMapLocalizationDictionary, LeafletMapLocalizationTextMapKey } from '@Primitive/Map/LeafletMapLocalization';
import {
    GeTimeSeriesSavedDataChartLocalizationDictionary, GetTSSeriesSavedDataSettingsLocalizationDictionary,
    TimeSeriesSavedDataChartLocalizationTextMapKey, TSSeriesSavedDataSettingsLocalizationTextMapKey
} from '@UI/Widgets/Things/Charts/TimeSeriesSavedDataChartLocalization';

import { SearchBarGetLocalizationDictionary, SearchBarLocalizationTextMapKey } from '@Primitive/SearchBarLocalization';

export const enum LocalizationMapEnum {
    DateRangePickerMapKey = "Primitive_DateRangePicker",
    DateRangeToolItemMapKey = "Primitive_DateRangeToolItem",
}

const pageInfoForPrimitives: { [key: string]: { Name: string, key: string } } = {
    [ImageViewerStatics.LocalizationTextMapKey]: { Name: "Image viewer", key: ImageViewerStatics.LocalizationTextMapKey },
    [LocalizationMapEnum.DateRangePickerMapKey]: { Name: "Date range picker", key: LocalizationMapEnum.DateRangePickerMapKey },
    [LocalizationMapEnum.DateRangeToolItemMapKey]: { Name: "Date range toolbar", key: LocalizationMapEnum.DateRangeToolItemMapKey },
    [LeafletMapLocalizationTextMapKey]: { Name: "Map", key: LeafletMapLocalizationTextMapKey },
    [TimeSeriesSavedDataChartLocalizationTextMapKey]: { Name: "Time series saved data chart", key: TimeSeriesSavedDataChartLocalizationTextMapKey },
    [TSSeriesSavedDataSettingsLocalizationTextMapKey]: { Name: "Time series saved data chart settings", key: TSSeriesSavedDataSettingsLocalizationTextMapKey },
    [SearchBarLocalizationTextMapKey]: { Name: "Search bar", key: SearchBarLocalizationTextMapKey },
    [VideoPlayerLocalizationTextMapKey]: { Name: "Video player", key: VideoPlayerLocalizationTextMapKey },
    [QueryBuilderLocalizationTextMapKey]: { Name: "Query builder", key: QueryBuilderLocalizationTextMapKey },
    [SelectLocalizationTextMapKey]: { Name: "Select", key: SelectLocalizationTextMapKey },
    [CalendarLocalizationTextMapKey]: { Name: "Calendar", key: CalendarLocalizationTextMapKey },
};
// ================================================================================================

const __LOCALIZATION_STORAGE_VERSION__ = "0.0.1";
export interface LocalizationSettings {
    /**
     * Version of the settings scheme.
     */
    Version: string;

    /**
     * The selected language based on ISO 639-2 codes
     * http://www.loc.gov/standards/iso639-2/php/code_list.php
     */
    SelectedLanguage: string;
}

export const LocalizationDefaultKey = "default";
// ================================================================================================
export type LocalizationMap = { [key: string]: string };
export type LocalizationDictionaryMap = { [key: string]: LocalizationMap };
// ================================================================================================
export interface LocalizationDictionary {
    /**
     * Language Dictionary
     * Dictionary[GroupKey][Key]
     * e.x. Dictionary[WidgetThingKey][Key]
     */
    Dictionary: LocalizationDictionaryMap;

    /**
     * Language default overrides
     */
    DictionaryOverrides?: LocalizationDictionaryMap;

    /**
     * Language UI info
     */
    Info: LocalizationLanguageInfo;
}
// ================================================================================================
export interface LocalizationLanguageInfo {
    /**
     * Language code based on ISO 639-2.
     * http://www.loc.gov/standards/iso639-2/php/code_list.php
     */
    Code: string;

    /**
     * UI label
     */
    Label: string;

    /**
     * Flag icon based on
     */
    FlagIcon: string;

    /**
     * Enable the showing of the language
     */
    IsEnabled: boolean;

    /**
     * Set if this is the default
     */
    IsDefault: boolean;
}
// ================================================================================================
export interface LocalizationChangeLanguageReq {
    /**
     * Language string
     */
    Language: string;
    /**
     * Select whether board should be reloaded
     */
    ReloadBoard: boolean;
}
// ================================================================================================
export class Localization {
    // --------------------------------------------------------------------------------------------
    private static _instanceDict: { [key: string]: Localization } = {};
    public static GetInstance(key: string): Localization { return (_.has(this._instanceDict, key)) ? this._instanceDict[key] : null; }
    // --------------------------------------------------------------------------------------------
    public static TryGetPageInfo(gkey: string): { Name: string, key: string } {
        if (gkey == "Primitive_DashboardManager")
            return { Name: "Board", key: gkey };
        if (_.startsWith(gkey, "Primitive"))
            return { Name: "Primitive", key: gkey };
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public static TryGetSubPageInfo(gkey: string, key: string): { Name: string, key: string } {
        if (isDefined(Localization.TryGetPageInfo(gkey)))
            return pageInfoForPrimitives[key];
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public get SelectedLanguage(): string { return this.Settings.SelectedLanguage }
    public PendingSaveChanges: boolean;
    private Settings: LocalizationSettings;
    private key: string;
    private Languages: { [code: string]: LocalizationDictionary } = {};
    private CacheLanguages: { [code: string]: LocalizationDictionary } = {};

    /**
     * Default language for the designer.
     */
    public DefaultLanguage: string;
    constructor(key: string, selectedLanguage: string) {
        this.key = key;
        this.PendingSaveChanges = false;

        // Set global access to dictionary
        setGlobal("Localization", Localization._instanceDict);

        // Detect if we already have instance with the same key
        if (_.has(Localization._instanceDict, key)) {
            DebugEx.TraceError("Try to init a second time the same localization", key);
        }

        // Link the singleton of this constractor to the generic map
        Localization._instanceDict[key] = this;

        // ------------------------------ +++ temporary changes
        const initialDictionary = {};
        // fill dictionary with primitives
        initialDictionary[ImageViewerStatics.LocalizationTextMapKey] = ImageViewerStatics.GetLocalizationDictionary();
        initialDictionary[LocalizationMapEnum.DateRangePickerMapKey] = GetDateRangePickerLocalizationDictionary();
        initialDictionary[LocalizationMapEnum.DateRangeToolItemMapKey] = GetDateRangeToolItemLocalizationDictionary();
        initialDictionary[LeafletMapLocalizationTextMapKey] = LeafletMapLocalizationDictionary;
        initialDictionary[ReactTableStatics.LocalizationTextMapKey] = ReactTableStatics.GetLocalizationDictionary();
        initialDictionary[CVViewerWidgetMapKey] = GetCVViewerWidgetLocalizationDictionary();
        initialDictionary[TimeSeriesSavedDataChartLocalizationTextMapKey] = GeTimeSeriesSavedDataChartLocalizationDictionary();
        initialDictionary[TSSeriesSavedDataSettingsLocalizationTextMapKey] = GetTSSeriesSavedDataSettingsLocalizationDictionary();
        initialDictionary[SearchBarLocalizationTextMapKey] = SearchBarGetLocalizationDictionary();
        initialDictionary[VideoPlayerLocalizationTextMapKey] = GetVideoPlayerLocalizationDictionary();
        initialDictionary[QueryBuilderLocalizationTextMapKey] = GetQueryBuilderLocalizationDictionary();
        initialDictionary[SelectLocalizationTextMapKey] = GetSelectLocalizationDictionary();
        initialDictionary[CalendarLocalizationTextMapKey] = GetCalendarLocalizationDictionary();
        // ------------------------------ --- temporary changes

        // Init default language
        this.DefaultLanguage = LocalizationDefaultKey;
        this.Languages[LocalizationDefaultKey] = { Info: { Code: LocalizationDefaultKey, Label: "English", FlagIcon: "svg/flags/united states", IsEnabled: true, IsDefault: this.DefaultLanguage == LocalizationDefaultKey }, Dictionary: _.cloneDeep(initialDictionary) };

        this.Settings = JSON.parse(sessionStorage.getItem("LocalizationSettings_" + this.key));
        if (this.Settings == null ||
            this.Settings.Version != __LOCALIZATION_STORAGE_VERSION__) {
            // Default values
            this.Settings = {
                Version: __LOCALIZATION_STORAGE_VERSION__,
                SelectedLanguage: selectedLanguage
            };
            this.SaveSettings();
        }

        // Send signal to WebNode to update a common key 
        let webNode = WebNode.GetInstanceKey(this.key);
        if (isDefined(webNode)) {
            webNode.UpdateLanguageCode(this.SelectedLanguage);
        }
    }
    // --------------------------------------------------------------------------------------------
    private InvalidCacheLang(lang: string) {
        delete this.CacheLanguages[lang];
    }
    // --------------------------------------------------------------------------------------------
    private SaveSettings() {
        sessionStorage.setItem("LocalizationSettings_" + this.key, JSONSafeStringify(this.Settings));
    }
    // --------------------------------------------------------------------------------------------
    private InitLanguage(langCode: string) {
        this.Languages[langCode] = {
            Info: { Code: langCode, Label: langCode, FlagIcon: "", IsEnabled: true, IsDefault: false }, // TODO: find the label
            Dictionary: {},
            DictionaryOverrides: {}
        };
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Change the Selected language
     */
    public ChangeSelectedLanguage(changeReq: LocalizationChangeLanguageReq) {
        // Check that the language exist
        if (!_.has(this.Languages, changeReq.Language))
            return;

        if (this.Settings.SelectedLanguage != changeReq.Language) {
            // Save the settings to browser storage
            this.Settings.SelectedLanguage = changeReq.Language;
            this.SaveSettings();
        }

        // Refresh the page
        if (changeReq.ReloadBoard == true) {
            document.location.reload();
        }

        /*// Send signal to WebNode to update a common key 
        let webNode = WebNode.GetInstanceKey(this.key);
        if (isDefined(webNode)) {
            webNode.UpdateLanguageCode(this.SelectedLanguage);
        }*/
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Change the Default language
     */
    public ChangeDefaultLanguage(lang: string) {
        if (!_.has(this.Languages, lang))
            return;
        this.DefaultLanguage = lang;
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Add text to dictionary for specific language
     */
    public AddTranslateText(langCode: string, groupKey: string, subKey: string, text: string) {
        // Init language
        if (!_.has(this.Languages, langCode))
            this.InitLanguage(langCode);

        if (langCode == LocalizationDefaultKey) {
            // Init dictionary
            if (this.Languages[langCode].DictionaryOverrides == null)
                this.Languages[langCode].DictionaryOverrides = {};

            if (!_.has(this.Languages[langCode].DictionaryOverrides, groupKey))
                this.Languages[langCode].DictionaryOverrides[groupKey] = {};

            // Add dictionary
            this.Languages[langCode].DictionaryOverrides[groupKey][subKey] = text;
        } else {
            // Init dictionary
            if (!_.has(this.Languages[langCode].Dictionary, groupKey))
                this.Languages[langCode].Dictionary[groupKey] = {};

            // Add dictionary
            this.Languages[langCode].Dictionary[groupKey][subKey] = text;
        }
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    public AddTexts(langCode: string, groupKey: string, texts: LocalizationMap) {
        // Init language
        if (!_.has(this.Languages, langCode))
            this.InitLanguage(langCode);
        if (!_.has(this.Languages[langCode].Dictionary, groupKey))
            this.Languages[langCode].Dictionary[groupKey] = {};

        // merge the arrived dictionary with the new one
        _.assign(this.Languages[langCode].Dictionary[groupKey], texts);
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    public ReplaceTexts(langCode: string, groupKey: string, texts: LocalizationMap) {
        // Init language
        if (!_.has(this.Languages, langCode))
            this.InitLanguage(langCode);
        if (!_.has(this.Languages[langCode].Dictionary, groupKey))
            this.Languages[langCode].Dictionary[groupKey] = {};

        // replace the texts
        this.Languages[langCode].Dictionary[groupKey] = texts;
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Get the text for the selected language
     */
    public GetTextMap(groupKey: string): LocalizationMap {
        let lang = this.Settings.SelectedLanguage;
        if (!_.has(this.Languages, this.Settings.SelectedLanguage))
            lang = this.DefaultLanguage;

        if (!_.has(this.Languages[lang].Dictionary, groupKey) &&
            !_.has(this.Languages[this.DefaultLanguage].Dictionary, groupKey))
            return {};

        // if is exist return the cached one
        if (_.has(this.CacheLanguages, lang) &&
            _.has(this.CacheLanguages[lang].Dictionary, groupKey))
            return this.CacheLanguages[lang].Dictionary[groupKey];

        // If the cache is not have the language then copy it from the default 
        if (!_.has(this.CacheLanguages, lang))
            this.CacheLanguages[lang] = _.cloneDeep(this.Languages[lang]);

        // Fill the cache and return it;
        for (let key of _.keys(this.Languages[this.DefaultLanguage].Dictionary)) {
            this.CacheLanguages[lang].Dictionary[key] = _.assign({},
                this.Languages[this.DefaultLanguage].Dictionary[key],
                _.has(this.Languages[this.DefaultLanguage].DictionaryOverrides, key) ? this.Languages[this.DefaultLanguage].DictionaryOverrides[key] : {},
                _.has(this.Languages[lang].Dictionary, key) ? this.Languages[lang].Dictionary[key] : {},
                _.has(this.Languages[lang].DictionaryOverrides, key) ? this.Languages[lang].DictionaryOverrides[key] : {},
            );
        }

        return this.CacheLanguages[lang].Dictionary[groupKey];
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Get the text for the selected language
     */
    public GetSpecificTextMap(lang: string, groupKey: string, getOverrides: boolean): LocalizationMap {
        if (!_.has(this.Languages, lang))
            return {};

        let dictionary = (getOverrides) ? this.Languages[lang].DictionaryOverrides : this.Languages[lang].Dictionary;
        if (!_.has(dictionary, groupKey))
            return {};

        return dictionary[groupKey];
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Delete text map for specific main key.
     */
    public DeleteTextMap(langCode: string, groupKey: string) {
        if (!_.has(this.Languages, langCode))
            return;
        this.Languages[langCode].Dictionary[groupKey] = {};
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    public GetSupportedLanguages(): { [key: string]: LocalizationLanguageInfo } {
        let info = {};
        _.forOwn(this.Languages, (l) => {
            info[l.Info.Code] = l.Info;
        });
        return info;
    }
    // --------------------------------------------------------------------------------------------
    public SetLanguageInfo(info: LocalizationLanguageInfo) {
        let lang = this.Languages[info.Code];
        if (isDefined(lang)) {
            lang.Info = info;
        } else {
            this.Languages[info.Code] = {
                Dictionary: {},
                DictionaryOverrides: {},
                Info: info
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguageGroups(code: string) {
        let lang = this.Languages[code];
        if (isDefined(lang)) {
            return _.keys(lang.Dictionary);
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public DeleteLanguage(code: string) {
        if (code == this.DefaultLanguage) {
            DebugEx.TraceError("Try to delate the default language.");
            return false;
        }

        // check if we delete the selected language
        if (code == this.SelectedLanguage) {
            let req: LocalizationChangeLanguageReq = { Language: this.DefaultLanguage, ReloadBoard: true };
            this.ChangeSelectedLanguage(req);
        }

        delete this.Languages[code];
        this.InvalidCacheLang(code);
        return true;
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguageDictionary(code: string): LocalizationDictionary {
        return this.Languages[code];
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguages() {
        return this.Languages;
    }
    // --------------------------------------------------------------------------------------------
    public UpdateTranslatedLanguage(code: string, dictionary: LocalizationDictionary) {
        /*
                var res = dictionarySchemaValidator().DataValidateString(dictionary);
                if (!res.valid) {
                    // todo: handle schema errors
                    DebugEx.TraceError("Error in Localization.UpdateTranslatedLanguage()! Errors: " + res.errors)
                    return false;
                }
        */
        if (code == LocalizationDefaultKey) {
            //DebugEx.TraceError("Try to update the default language.");
            this.Languages[code].Info = dictionary.Info;
            this.Languages[code].DictionaryOverrides = dictionary.DictionaryOverrides;
            return false;
        }

        // Add/Update language translation
        this.Languages[code] = dictionary;
        this.InvalidCacheLang(code);
        return true;
    }
    // --------------------------------------------------------------------------------------------
}
// --------------------------------------------------------------------------------------------
const dictionarySchemaValidator = () => JsonSchemaValidator.GetInstance('LocalizationDictionaryValidator', {
    title: "LocalizationDictionary",
    type: "object",
    minProperties: 1,
    patternProperties: {
        "^.*$": {
            type: "object",
            "^.*$": {
                type: 'string'
            }
        }
    }
});
// ================================================================================================